import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wordmark } from "../components/Wordmark";
import { useSeo } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "vynl_admin_pw";

export default function Admin() {
  useSeo("/admin", { noindex: true });
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState(false);
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [newsletter, setNewsletter] = useState({ counts: { pending: 0, confirmed: 0, unsubscribed: 0 }, subscribers: [] });
  const [contacts, setContacts] = useState({ total: 0, unresolved: 0, counts_by_type: {}, enquiries: [] });
  const [contactFilter, setContactFilter] = useState("All");
  const [contactShowResolved, setContactShowResolved] = useState(false);
  const [founding, setFounding] = useState({ total: 100, confirmed: 0, remaining: 100, reveal_threshold: 20, show_count: false });
  const [foundingInput, setFoundingInput] = useState("");
  const [foundingSaving, setFoundingSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchApps = useCallback(async (password) => {
    setLoading(true);
    try {
      const [appsRes, statsRes, newsRes, contactsRes, foundingRes] = await Promise.all([
        axios.get(`${API}/admin/applications`, { headers: { "X-Admin-Password": password } }),
        axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Password": password } }),
        axios.get(`${API}/admin/newsletter`, { headers: { "X-Admin-Password": password } }),
        axios.get(`${API}/admin/contacts`, { headers: { "X-Admin-Password": password } }),
        axios.get(`${API}/founding/spots`),
      ]);
      setApps(appsRes.data);
      setStats(statsRes.data);
      setNewsletter(newsRes.data);
      setContacts(contactsRes.data);
      setFounding(foundingRes.data);
      setFoundingInput(String(foundingRes.data?.confirmed ?? 0));
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, password);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid admin password.");
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
      } else {
        toast.error("Could not load submissions.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) fetchApps(saved);
  }, [fetchApps]);

  const login = (e) => {
    e.preventDefault();
    if (!pw) return;
    fetchApps(pw);
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setApps([]);
    setStats(null);
    setNewsletter({ counts: { pending: 0, confirmed: 0, unsubscribed: 0 }, subscribers: [] });
    setContacts({ total: 0, unresolved: 0, counts_by_type: {}, enquiries: [] });
    setContactFilter("All");
    setContactShowResolved(false);
    setFounding({ total: 100, confirmed: 0, remaining: 100, reveal_threshold: 20, show_count: false });
    setFoundingInput("");
    setPw("");
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  const csvCell = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const downloadCsv = () => {
    if (!apps.length) {
      toast.error("No applications to export.");
      return;
    }
    const headers = ["Name", "Artist name", "Instagram", "Email", "Consent", "Submitted"];
    const rows = apps.map((a) => [
      a.name, a.artist_name, a.instagram, a.email,
      a.consent ? "Yes" : "No", fmt(a.created_at),
    ]);
    const csv = [headers, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
    // BOM so Excel opens UTF-8 correctly
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `vynlworld-applications-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${apps.length} application${apps.length === 1 ? "" : "s"}.`);
  };

  const downloadNewsletterCsv = () => {
    // Compliance: only ever export confirmed subscribers. Pending users have not
    // finished double opt-in, and unsubscribed users have withdrawn consent.
    const confirmed = (newsletter.subscribers || []).filter((s) => s.status === "confirmed");
    if (!confirmed.length) {
      toast.error("No confirmed subscribers to export.");
      return;
    }
    const headers = ["Email", "Source", "Signed up", "Confirmed"];
    const rows = confirmed.map((s) => [s.email, s.source, fmt(s.signup_at), fmt(s.confirmed_at)]);
    const csv = [headers, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `vynlworld-newsletter-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${confirmed.length} confirmed subscriber${confirmed.length === 1 ? "" : "s"}.`);
  };

  // ---- Contacts ----
  const contactTypes = ["All", "General", "Artist", "Designer", "Press & media", "Partnership", "Other"];
  const filteredContacts = (contacts.enquiries || []).filter((c) => {
    if (contactFilter !== "All" && c.enquiry_type !== contactFilter) return false;
    if (!contactShowResolved && c.resolved) return false;
    return true;
  });

  const toggleContactResolved = async (id, resolved) => {
    const password = sessionStorage.getItem(STORAGE_KEY);
    try {
      await axios.patch(
        `${API}/admin/contacts/${id}`,
        { resolved },
        { headers: { "X-Admin-Password": password } },
      );
      // Optimistically update the local state and unresolved counter.
      setContacts((prev) => {
        const next = prev.enquiries.map((c) =>
          c.id === id ? { ...c, resolved, resolved_at: resolved ? new Date().toISOString() : null } : c
        );
        const unresolved = next.filter((c) => !c.resolved).length;
        return { ...prev, enquiries: next, unresolved };
      });
      toast.success(resolved ? "Marked resolved." : "Reopened.");
    } catch {
      toast.error("Could not update. Try again.");
    }
  };

  const downloadContactsCsv = () => {
    if (!filteredContacts.length) {
      toast.error("No enquiries to export in the current view.");
      return;
    }
    const headers = ["Name", "Email", "Type", "Message", "Resolved", "Received", "Resolved at"];
    const rows = filteredContacts.map((c) => [
      c.name, c.email, c.enquiry_type, c.message,
      c.resolved ? "Yes" : "No", fmt(c.created_at), c.resolved_at ? fmt(c.resolved_at) : "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `vynlworld-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredContacts.length} enquir${filteredContacts.length === 1 ? "y" : "ies"}.`);
  };

  // ---- Founding artist counter ----
  const updateFounding = async (body) => {
    const password = sessionStorage.getItem(STORAGE_KEY);
    setFoundingSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/founding`, body, {
        headers: { "X-Admin-Password": password },
      });
      setFounding((prev) => ({ ...prev, ...res.data, show_count: res.data.confirmed >= (prev.reveal_threshold ?? 20) }));
      setFoundingInput(String(res.data.confirmed));
      toast.success(`Confirmed founding artists: ${res.data.confirmed} / ${res.data.total}.`);
    } catch (err) {
      toast.error("Could not update. Try again.");
    } finally {
      setFoundingSaving(false);
    }
  };

  const saveFoundingAbsolute = () => {
    const n = parseInt(foundingInput, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error("Enter a whole number 0 or greater.");
      return;
    }
    updateFounding({ confirmed: n });
  };

  if (!authed) {
    return (
      <div className="grain min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "#0E0A16" }} data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-[420px] p-10" style={{ backgroundColor: "#1A1424", border: "1px solid #2A2233" }}>
          <Wordmark className="h-6" />
          <h1 className="font-display text-2xl mt-8 mb-2" style={{ color: "#F4EEF7" }}>Admin access</h1>
          <p className="text-sm mb-6" style={{ color: "#9B8FAC" }}>Founding artist submissions. Authorised personnel only.</p>
          <label htmlFor="admin-password-input" className="block text-[11px] uppercase tracking-[0.24em] mb-2" style={{ color: "#9B8FAC" }}>Password</label>
          <input
            id="admin-password-input"
            data-testid="admin-password-input"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="••••••••••"
            className="w-full px-4 py-3 text-base outline-none transition-colors duration-300 focus:border-[#C724B1]"
            style={{ backgroundColor: "#0E0A16", border: "1px solid #2A2233", color: "#F4EEF7" }}
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="btn-sharp w-full mt-6 text-xs uppercase tracking-[0.22em] px-8 py-4 font-medium disabled:opacity-60"
            style={{ backgroundColor: "#C724B1", color: "#F4EEF7" }}
          >
            {loading ? "Checking…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen" style={{ backgroundColor: "#0E0A16" }} data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 flex items-center justify-between px-6 md:px-10 h-[72px]" style={{ backgroundColor: "rgba(14,10,22,0.82)", backdropFilter: "blur(18px)", borderBottom: "1px solid #2A2233" }}>
        <Wordmark className="h-5" />
        <div className="flex items-center gap-6">
          {contacts.unresolved > 0 && (
            <a
              href="#admin-contacts-section"
              data-testid="admin-unresolved-badge"
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:text-[#F4EEF7]"
              style={{ color: "#C724B1" }}
              title="Jump to unresolved enquiries"
            >
              <span
                className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-2 text-[11px] font-medium"
                style={{ backgroundColor: "#C724B1", color: "#F4EEF7" }}
              >
                {contacts.unresolved}
              </span>
              Unresolved
            </a>
          )}
          <span className="text-xs uppercase tracking-[0.2em]" style={{ color: "#9B8FAC" }}>
            {apps.length} submission{apps.length === 1 ? "" : "s"}
          </span>
          <button data-testid="admin-refresh-btn" onClick={() => fetchApps(sessionStorage.getItem(STORAGE_KEY))} className="text-xs uppercase tracking-[0.2em] hover:text-[#C724B1] transition-colors duration-300" style={{ color: "#F4EEF7" }}>Refresh</button>
          <button
            data-testid="admin-export-csv-btn"
            onClick={downloadCsv}
            disabled={!apps.length}
            className="btn-sharp text-xs uppercase tracking-[0.2em] px-4 py-2 font-medium disabled:opacity-40"
            style={{ backgroundColor: "#C724B1", color: "#F4EEF7" }}
          >
            Download CSV
          </button>
          <button data-testid="admin-logout-btn" onClick={logout} className="text-xs uppercase tracking-[0.2em] hover:text-[#C724B1] transition-colors duration-300" style={{ color: "#F4EEF7" }}>Log out</button>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 md:px-10 py-16">
        {stats && (
          <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-4 gap-px mb-14" style={{ backgroundColor: "#2A2233" }}>
            {[
              { label: "Total applications", value: stats.total_applications, testid: "stat-total-apps", gold: true },
              { label: "Pageviews (all time)", value: stats.totals?.pageview ?? 0, testid: "stat-pageviews" },
              { label: "Pageviews (today)", value: stats.today?.pageview ?? 0, testid: "stat-pageviews-today" },
              { label: "Apply submits (all time)", value: stats.totals?.apply_submit ?? 0, testid: "stat-submits" },
              { label: "Interest (all time)", value: stats.totals?.interest ?? 0, testid: "stat-interest" },
              { label: "Interest (today)", value: stats.today?.interest ?? 0, testid: "stat-interest-today" },
              { label: "Shares (all time)", value: stats.totals?.share ?? 0, testid: "stat-shares" },
              { label: "Shares (today)", value: stats.today?.share ?? 0, testid: "stat-shares-today" },
            ].map((s) => (
              <div key={s.testid} data-testid={s.testid} className="p-6" style={{ backgroundColor: "#1A1424" }}>
                <div className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: "#9B8FAC" }}>{s.label}</div>
                <div className="font-display text-3xl md:text-4xl" style={{ color: s.gold ? "#F0C420" : "#F4EEF7" }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs mb-8" style={{ color: "#9B8FAC" }}>
          Analytics are cookieless and aggregate-only — no personal data, no third-party trackers.
        </p>

        {/* --- Founding artist counter control --- */}
        <div
          data-testid="admin-founding-section"
          className="mb-14 p-6 md:p-8"
          style={{ backgroundColor: "#1A1424", border: "1px solid #2A2233" }}
        >
          <div className="flex flex-col lg:flex-row lg:items-end gap-6 lg:gap-10">
            <div className="lg:flex-1">
              <div className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: "#9B8FAC" }}>Founding artists — scarcity control</div>
              <div className="flex items-baseline gap-3 mb-2">
                <span data-testid="founding-admin-confirmed" className="font-display text-4xl md:text-5xl" style={{ color: "#F0C420" }}>{founding.confirmed}</span>
                <span className="text-base" style={{ color: "#9B8FAC" }}>/ {founding.total} confirmed</span>
              </div>
              <div className="text-xs" style={{ color: "#9B8FAC" }}>
                <span data-testid="founding-admin-remaining">{founding.remaining}</span> spots remaining · public counter{" "}
                <span style={{ color: founding.show_count ? "#F0C420" : "#C724B1" }}>{founding.show_count ? "visible" : `hidden (below threshold of ${founding.reveal_threshold})`}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
              <div>
                <label htmlFor="founding-input" className="block text-[11px] uppercase tracking-[0.22em] mb-2" style={{ color: "#9B8FAC" }}>Set confirmed</label>
                <input
                  id="founding-input"
                  data-testid="admin-founding-input"
                  type="number"
                  min="0"
                  max={founding.total}
                  value={foundingInput}
                  onChange={(e) => setFoundingInput(e.target.value)}
                  className="w-32 px-4 py-3 text-base outline-none transition-colors duration-300 focus:border-[#C724B1]"
                  style={{ backgroundColor: "#0E0A16", border: "1px solid #2A2233", color: "#F4EEF7" }}
                />
              </div>
              <button
                data-testid="admin-founding-save-btn"
                onClick={saveFoundingAbsolute}
                disabled={foundingSaving}
                className="btn-sharp text-xs uppercase tracking-[0.22em] px-5 py-3 font-medium disabled:opacity-60"
                style={{ backgroundColor: "#C724B1", color: "#F4EEF7" }}
              >
                {foundingSaving ? "Saving…" : "Save"}
              </button>
              <div className="flex gap-2">
                <button
                  data-testid="admin-founding-dec-btn"
                  onClick={() => updateFounding({ increment: -1 })}
                  disabled={foundingSaving || founding.confirmed <= 0}
                  className="text-lg px-4 py-3 transition-colors duration-200 disabled:opacity-40"
                  style={{ border: "1px solid #2A2233", color: "#F4EEF7" }}
                >
                  −1
                </button>
                <button
                  data-testid="admin-founding-inc-btn"
                  onClick={() => updateFounding({ increment: 1 })}
                  disabled={foundingSaving || founding.confirmed >= founding.total}
                  className="text-lg px-4 py-3 transition-colors duration-200 disabled:opacity-40"
                  style={{ border: "1px solid #2A2233", color: "#F4EEF7" }}
                >
                  +1
                </button>
              </div>
            </div>
          </div>
        </div>

        <h1 className="font-display text-4xl md:text-5xl mb-10" style={{ color: "#F4EEF7" }}>Founding artist applications</h1>

        {apps.length === 0 ? (
          <p className="text-base" style={{ color: "#9B8FAC" }} data-testid="admin-empty">No submissions yet.</p>
        ) : (
          <div className="overflow-x-auto" style={{ border: "1px solid #2A2233" }}>
            <table className="w-full text-left border-collapse" data-testid="admin-table">
              <thead>
                <tr style={{ backgroundColor: "#1A1424" }}>
                  {["Artist name", "Name", "Instagram", "Email", "Received"].map((h) => (
                    <th key={h} className="px-5 py-4 text-[11px] uppercase tracking-[0.22em] font-medium" style={{ color: "#9B8FAC", borderBottom: "1px solid #2A2233" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={a.id} data-testid={`admin-row-${a.id}`} className="transition-colors duration-200 hover:bg-[#1A1424]">
                    <td className="px-5 py-4 font-display text-base" style={{ color: "#C724B1", borderBottom: "1px solid #2A2233" }}>{a.artist_name}</td>
                    <td className="px-5 py-4 text-sm" style={{ color: "#F4EEF7", borderBottom: "1px solid #2A2233" }}>{a.name}</td>
                    <td className="px-5 py-4 text-sm" style={{ color: "#9B8FAC", borderBottom: "1px solid #2A2233" }}>{a.instagram}</td>
                    <td className="px-5 py-4 text-sm" style={{ borderBottom: "1px solid #2A2233" }}>
                      <a href={`mailto:${a.email}`} className="hover:text-[#C724B1] transition-colors duration-300" style={{ color: "#F4EEF7" }}>{a.email}</a>
                    </td>
                    <td className="px-5 py-4 text-sm" style={{ color: "#9B8FAC", borderBottom: "1px solid #2A2233" }}>{fmt(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* --- Newsletter section --- */}
        <div className="mt-20" data-testid="admin-newsletter-section">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: "#9B8FAC" }}>Subscribers</div>
              <h2 className="font-display text-3xl md:text-4xl" style={{ color: "#F4EEF7" }}>Newsletter</h2>
            </div>
            <button
              data-testid="admin-newsletter-export-btn"
              onClick={downloadNewsletterCsv}
              disabled={!newsletter.counts.confirmed}
              className="btn-sharp text-xs uppercase tracking-[0.2em] px-4 py-2 font-medium disabled:opacity-40 self-start md:self-auto"
              style={{ backgroundColor: "#C724B1", color: "#F4EEF7" }}
            >
              Export confirmed (CSV)
            </button>
          </div>

          <div className="grid grid-cols-3 gap-px mb-10" style={{ backgroundColor: "#2A2233" }}>
            {[
              { label: "Confirmed", value: newsletter.counts.confirmed, testid: "newsletter-count-confirmed", gold: true },
              { label: "Pending", value: newsletter.counts.pending, testid: "newsletter-count-pending" },
              { label: "Unsubscribed", value: newsletter.counts.unsubscribed, testid: "newsletter-count-unsubscribed" },
            ].map((s) => (
              <div key={s.testid} data-testid={s.testid} className="p-6" style={{ backgroundColor: "#1A1424" }}>
                <div className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: "#9B8FAC" }}>{s.label}</div>
                <div className="font-display text-3xl md:text-4xl" style={{ color: s.gold ? "#F0C420" : "#F4EEF7" }}>{s.value}</div>
              </div>
            ))}
          </div>

          {!newsletter.subscribers.length ? (
            <p className="text-base" style={{ color: "#9B8FAC" }} data-testid="admin-newsletter-empty">No subscribers yet.</p>
          ) : (
            <div className="overflow-x-auto" style={{ border: "1px solid #2A2233" }}>
              <table className="w-full text-left border-collapse" data-testid="admin-newsletter-table">
                <thead>
                  <tr style={{ backgroundColor: "#1A1424" }}>
                    {["Email", "Status", "Source", "Signed up", "Confirmed", "Unsubscribed"].map((h) => (
                      <th key={h} className="px-5 py-4 text-[11px] uppercase tracking-[0.22em] font-medium" style={{ color: "#9B8FAC", borderBottom: "1px solid #2A2233" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {newsletter.subscribers.map((s) => {
                    const statusColor =
                      s.status === "confirmed" ? "#F0C420" :
                      s.status === "pending" ? "#C724B1" : "#9B8FAC";
                    return (
                      <tr key={s.id} data-testid={`admin-newsletter-row-${s.id}`} className="transition-colors duration-200 hover:bg-[#1A1424]">
                        <td className="px-5 py-4 text-sm" style={{ borderBottom: "1px solid #2A2233" }}>
                          <a href={`mailto:${s.email}`} className="hover:text-[#C724B1] transition-colors duration-300" style={{ color: "#F4EEF7" }}>{s.email}</a>
                        </td>
                        <td className="px-5 py-4 text-[11px] uppercase tracking-[0.22em]" style={{ color: statusColor, borderBottom: "1px solid #2A2233" }}>{s.status}</td>
                        <td className="px-5 py-4 text-sm" style={{ color: "#9B8FAC", borderBottom: "1px solid #2A2233" }}>{s.source}</td>
                        <td className="px-5 py-4 text-sm" style={{ color: "#9B8FAC", borderBottom: "1px solid #2A2233" }}>{fmt(s.signup_at)}</td>
                        <td className="px-5 py-4 text-sm" style={{ color: "#9B8FAC", borderBottom: "1px solid #2A2233" }}>{s.confirmed_at ? fmt(s.confirmed_at) : "—"}</td>
                        <td className="px-5 py-4 text-sm" style={{ color: "#9B8FAC", borderBottom: "1px solid #2A2233" }}>{s.unsubscribed_at ? fmt(s.unsubscribed_at) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* --- Enquiries section --- */}
        <div className="mt-20" data-testid="admin-contacts-section" id="admin-contacts-section">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: "#9B8FAC" }}>General enquiries</div>
              <h2 className="font-display text-3xl md:text-4xl" style={{ color: "#F4EEF7" }}>Enquiries</h2>
            </div>
            <button
              data-testid="admin-contacts-export-btn"
              onClick={downloadContactsCsv}
              disabled={!filteredContacts.length}
              className="btn-sharp text-xs uppercase tracking-[0.2em] px-4 py-2 font-medium disabled:opacity-40 self-start md:self-auto"
              style={{ backgroundColor: "#C724B1", color: "#F4EEF7" }}
            >
              Export view (CSV)
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px mb-10" style={{ backgroundColor: "#2A2233" }}>
            <div data-testid="contact-count-total" className="p-6" style={{ backgroundColor: "#1A1424" }}>
              <div className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: "#9B8FAC" }}>Total</div>
              <div className="font-display text-3xl md:text-4xl" style={{ color: "#F4EEF7" }}>{contacts.total}</div>
            </div>
            <div data-testid="contact-count-unresolved" className="p-6" style={{ backgroundColor: "#1A1424" }}>
              <div className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: "#9B8FAC" }}>Unresolved</div>
              <div className="font-display text-3xl md:text-4xl" style={{ color: contacts.unresolved > 0 ? "#C724B1" : "#F4EEF7" }}>{contacts.unresolved}</div>
            </div>
            {["Artist", "Press & media"].map((t) => (
              <div key={t} data-testid={`contact-count-${t.toLowerCase().replace(/\s|&/g, "-")}`} className="p-6" style={{ backgroundColor: "#1A1424" }}>
                <div className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: "#9B8FAC" }}>{t}</div>
                <div className="font-display text-3xl md:text-4xl" style={{ color: "#F4EEF7" }}>{contacts.counts_by_type?.[t] || 0}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-[0.22em] mr-2" style={{ color: "#9B8FAC" }}>Filter</span>
              {contactTypes.map((t) => (
                <button
                  key={t}
                  data-testid={`contact-filter-${t.toLowerCase().replace(/\s|&/g, "-")}`}
                  onClick={() => setContactFilter(t)}
                  className="text-[11px] uppercase tracking-[0.22em] px-3 py-2 transition-colors duration-200"
                  style={{
                    backgroundColor: contactFilter === t ? "#C724B1" : "#1A1424",
                    color: "#F4EEF7",
                    border: "1px solid " + (contactFilter === t ? "#C724B1" : "#2A2233"),
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
            <label
              data-testid="contact-show-resolved-toggle"
              className="flex items-center gap-2 cursor-pointer md:ml-auto"
              style={{ color: "#9B8FAC" }}
            >
              <input
                type="checkbox"
                checked={contactShowResolved}
                onChange={(e) => setContactShowResolved(e.target.checked)}
                className="h-4 w-4 appearance-none border cursor-pointer transition-colors duration-200"
                style={{ borderColor: "#2A2233", backgroundColor: contactShowResolved ? "#C724B1" : "#0E0A16" }}
              />
              <span className="text-[11px] uppercase tracking-[0.22em]">Show resolved</span>
            </label>
          </div>

          {!filteredContacts.length ? (
            <p className="text-base" style={{ color: "#9B8FAC" }} data-testid="admin-contacts-empty">No enquiries in this view.</p>
          ) : (
            <div className="overflow-x-auto" style={{ border: "1px solid #2A2233" }}>
              <table className="w-full text-left border-collapse" data-testid="admin-contacts-table">
                <thead>
                  <tr style={{ backgroundColor: "#1A1424" }}>
                    {["From", "Type", "Message", "Received", "Status", ""].map((h) => (
                      <th key={h} className="px-5 py-4 text-[11px] uppercase tracking-[0.22em] font-medium" style={{ color: "#9B8FAC", borderBottom: "1px solid #2A2233" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.map((c) => (
                    <tr key={c.id} data-testid={`admin-contact-row-${c.id}`} className="transition-colors duration-200 hover:bg-[#1A1424] align-top">
                      <td className="px-5 py-4 text-sm" style={{ borderBottom: "1px solid #2A2233" }}>
                        <div style={{ color: "#F4EEF7" }}>{c.name}</div>
                        <a href={`mailto:${c.email}`} className="text-xs hover:text-[#C724B1] transition-colors duration-300" style={{ color: "#9B8FAC" }}>{c.email}</a>
                      </td>
                      <td className="px-5 py-4 text-[11px] uppercase tracking-[0.22em]" style={{ color: "#F4EEF7", borderBottom: "1px solid #2A2233" }}>{c.enquiry_type}</td>
                      <td className="px-5 py-4 text-sm max-w-[360px]" style={{ color: "#9B8FAC", borderBottom: "1px solid #2A2233" }}>
                        <div className="whitespace-pre-wrap break-words">{c.message}</div>
                      </td>
                      <td className="px-5 py-4 text-sm whitespace-nowrap" style={{ color: "#9B8FAC", borderBottom: "1px solid #2A2233" }}>{fmt(c.created_at)}</td>
                      <td className="px-5 py-4 text-[11px] uppercase tracking-[0.22em]" style={{ color: c.resolved ? "#F0C420" : "#C724B1", borderBottom: "1px solid #2A2233" }}>{c.resolved ? "Resolved" : "Open"}</td>
                      <td className="px-5 py-4 text-sm whitespace-nowrap" style={{ borderBottom: "1px solid #2A2233" }}>
                        <button
                          data-testid={`admin-contact-toggle-${c.id}`}
                          onClick={() => toggleContactResolved(c.id, !c.resolved)}
                          className="text-[11px] uppercase tracking-[0.22em] px-3 py-2 transition-colors duration-200 hover:text-[#F4EEF7]"
                          style={{ border: "1px solid #2A2233", color: "#F4EEF7" }}
                        >
                          {c.resolved ? "Reopen" : "Mark resolved"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
